import React, { useMemo } from "react";
import { useHistory } from "react-router";
import styled from "styled-components";

import SearchIcon from "@atlaskit/icon/glyph/search";
import SendIcon from "@atlaskit/icon/glyph/send";
import CalendarIcon from "@atlaskit/icon/glyph/calendar";
import FileIcon from "@atlaskit/icon/glyph/file";
import WarningIcon from "@atlaskit/icon/glyph/warning";
import { getResourcesUrl, URL } from "../../constants/navigation";
import { handleNavigation } from "../../helpers/handleNavigation";
import {
  COLOR_ATHENS_GRAY_DARKER_2,
  COLOR_FUCHSIA_BLUE,
  COLOR_STALE_GRAY,
  COLOR_NIAGARA,
  COLOR_PACIFIC_BLUE,
  COLOR_TREE_POPPY,
  COLOR_WELL_READ,
  COLOR_WHITE,
  COLOR_SCIENCE_BLUE,
  COLOR_MILANO_RED,
} from "../../constants/styles";
import { CLIENTS_EMAIL_ADDRESS } from "../../constants/general";
import { dayjs } from "../../services/dayjs";
import { Grid } from "../../UI/Grid";
import { Button } from "../../UI/Button";
import { HelpTooltip } from "../HelpTooltip";
import { Link } from "../../UI/Link";
import { Typography } from "../../UI/Typography";
import { DashboardCard } from "../DashboardCard";
import { MatchedUsersDropdown } from "../MatchedUsersDropdown";
import { MatchingRoundChip } from "../MatchingRoundStatus";

import type { MatchDate, L2Statistics } from "./types";

const Container = styled.div`
  padding-top: 10px;
  padding-bottom: 40px;
  border-bottom: 2px solid ${COLOR_ATHENS_GRAY_DARKER_2};
`;

const TooltipTextContainer = styled.span`
  div {
    display: inline;
  }
`;

const WarningContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  color: ${COLOR_MILANO_RED};
  font-size: 14px;
`;

const WarningLink = styled.a`
  color: ${COLOR_MILANO_RED};
  text-decoration: underline;
  &:hover {
    text-decoration: underline;
  }
`;

// Utility function to determine if network is import-based
const isImportBasedNetwork = (importType: string | null): boolean => {
  return ["ENTRATA", "SFTP", "ACC", "CBORD", "RMS", "RMBG", "Kinetic", "Email"].includes(importType || "");
};

interface MatchingRoundHelperTexts {
  [key: string]: {
    title: string;
    text: string;
  };
}

interface IProps {
  apt: boolean;
  isIntegratedNetwork: boolean;
  isLoggedInUsersCard: boolean;
  placementWorksheetEnabled: boolean;
  subnetwork?: number;
  stats: L2Statistics;
  importType: string | null;
}

const getDateValue = (date?: dayjs.Dayjs | null) => date?.format("MM/DD/YYYY")|| undefined;

const GENERATED_LEASES_TOOLTIP_CONTENT =
  "RoomSync has been shown to be a powerful amenity that can help drive lease decisions. When a resident signs up for the app, they are asked if RoomSync impacted their decision to sign a lease.  ”Gained Leases” is the number of residents who says “Yes” to this question.";
const MATCHED_USERS_TOOLTIP_CONTENT = "The number of residents who are matched with at least 1 roommate in RoomSync.";
const PLACED_USERS_TOOLTIP_CONTENT = "The number of residents who have been placed in a row of the Placement Worksheet for unit availability tracking";
const INVITATION_SOURCE_TOOLTIP_CONTENT = "If you need to immediately run an import, please contact RoomSync’s Customer Support";
const INVITATION_SOURCE_TOOLTIP_CONTENT_KINETIC = "After selecting the export to RoomSync option, the resident should appear after a few minutes";
const INVITATION_SOURCE_TOOLTIP_CONTENT_EMAIL = "Users appear in the system within a few minutes after the file is sent by email.";

const TOOLTIP_HELP_CONTENTS: MatchingRoundHelperTexts = {
  "UPCOMING": {
    title: "Upcoming",
    text: "This upcoming matching round is not yet open for user registration or matching."
  },
  "ACTIVE": {
    title: "Registration Open",
    text: "Registration is currently available for this matching round. Users can sign up and create their matching profiles, but they are not yet able to view or connect with potential roommates",
  },
  "MATCHING": {
    title: "Matching Open",
    text: "This matching round is active for the matching process. Users can register, set up their matching profiles, and interact with other potential roommates by viewing and requesting matches."
  },
  "ARCHIVED": {
    title: "Upcoming",
    text: "This upcoming matching round is not yet open for user registration or matching."
  },
  "CLOSED": {
    title: "Closed",
    text: "This matching round is closed. Users cannot register, create their profiles or view and match with other potential roommates."
  },
};

const YEAR_AROUND_TOOLTIP_CONTENT = {
  text: "This means residents can register and match at any time with no end date. To change this setting, please contact the RoomSync team at clients@roomsync.com."
};

// Utility functions to determine invitation source and distribution method
const getInvitationSource = (importType: string | null): { text: string; url: string } => {
  switch (importType) {
    case "ENTRATA":
      return {
        text: "Entrata Integration (runs every 48 hours)",
        url: URL.INTEGRATIONS
      };
    case "SFTP":
    case "CBORD":
    case "RMS":
    case "RMBG":
      return {
        text: "Client Uploads SFTP",
        url: URL.INTEGRATIONS
      };
    case "ACC":
      return {
        text: "ACC Integration (runs every 48 hours)",
        url: URL.INTEGRATIONS
      };
    case "Kinetic":
      return {
        text: "Kinetic Integration",
        url: URL.INTEGRATIONS
      };
    case "Email":
      return {
        text: "Client Emails Data File",
        url: URL.INTEGRATIONS
      };
    case null:
    case "OPEN":
    case "PASSCODE":
    default:
      return {
        text: "Invite Links",
        url: URL.USERS_INVITE
      };
  }
};

const getInviteDistributionMethod = (importType: string | null): string => {
  switch (importType) {
    case "ENTRATA":
    case "SFTP":
    case "ACC":
    case "RMBG":
    case "Kinetic":
    case "RMS":
    case "CBORD":
    case "Email":
      return "RoomSync Auto-Email";
    case null:
    case "OPEN":
    case "PASSCODE":
    default:
      return "Client Sends Email";
  }
};



export const Row: React.FC<IProps & MatchDate> = ({
  l2Name,
  matchingStartDate,
  matchingEndDate,
  activationStartDate,
  activationEndDate,
  matchingRoundStatus,
  isIntegratedNetwork,
  isLoggedInUsersCard,
  stats,
  placementWorksheetEnabled,
  apt,
  importType,
}) => {
    const { push } = useHistory();
    const navTo = (url: string) => (event: React.MouseEvent) => {
      handleNavigation(event, url, push);
    };
    const status = matchingRoundStatus ? matchingRoundStatus : "";
    const handleResourcesClick = () => {
      window.open(getResourcesUrl(apt));
    };

    const areDatesNonEmpty = useMemo(
      () => activationEndDate && activationStartDate && matchingStartDate && activationEndDate,
      [activationEndDate, activationStartDate, matchingStartDate, matchingEndDate]
    );

    const invitationSource = getInvitationSource(importType);
    const inviteDistributionMethod = getInviteDistributionMethod(importType);

    const InvitedUsersTooltipText = (<TooltipTextContainer>
      <Grid item xs={12}>
        <Typography color={COLOR_WHITE} sx={{ fontSize: "14px !important" }}>
          The number of users that have been imported to RoomSync and sent an email invitation.
          Users that have not registered after a certain number of days will receive additional reminder emails to register according to your&nbsp;
          <Link onClick={navTo(URL.USER_NOTIFICATIONS)}>Invitation Reminder Email</Link>&nbsp;setting.
        </Typography>
      </Grid>
    </TooltipTextContainer>);

    return (
    <Container>
      <Grid container gap={1}>
        <Grid container gap={3}>
          <Grid container gap={3}>
            <Grid item>
              <Typography variant={"h6"}><strong>Matching Round: </strong> {l2Name}</Typography>
            </Grid>
          </Grid>
        </Grid>
        { areDatesNonEmpty &&
          <Grid container gap={3} alignItems="center">
            <Grid item>
              <Typography color={COLOR_STALE_GRAY}><strong>Registration Start: </strong>{getDateValue(activationStartDate)} </Typography>
            </Grid>
            <Grid item>
              <Typography color={COLOR_STALE_GRAY}><strong>Matching Start: </strong>{getDateValue(matchingStartDate)} </Typography>
            </Grid>
            <Grid item>
              <Typography color={COLOR_STALE_GRAY}><strong>Matching End: </strong>{getDateValue(matchingEndDate)}</Typography>
            </Grid>
            <Grid item>
              <Typography color={COLOR_STALE_GRAY}><strong>Registration End: </strong>{getDateValue(activationEndDate)}</Typography>
            </Grid>
            {status !== "ARCHIVED" && (
              <Grid item>
                {/* Link styling (saved for reference):
                  <Link
                    onClick={navTo(URL.MATCH_RULES)}
                    sx={{
                      color: COLOR_SCIENCE_BLUE,
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      padding: '4px 8px',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    Edit Dates
                  </Link>
                */}
                <Button
                  appearance="link"
                  onClick={navTo(URL.MATCH_RULES)}
                >
                  Edit Dates
                </Button>
              </Grid>
            )}
          </Grid>
        }
        <Grid container spacing={3} alignItems="center" justifyContent="flex-start">
          <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography color={COLOR_STALE_GRAY}>
              <strong>Status: </strong>
            </Typography>
            <HelpTooltip {...TOOLTIP_HELP_CONTENTS[status]} withIcon={false}>
              <MatchingRoundChip status={status} />
            </HelpTooltip>
          </Grid>
          <Grid item>
            <Typography color={COLOR_STALE_GRAY} component="span" sx={{ display: 'inline' }}>
              <strong>Invitation Source: </strong>
              {(importType === "ACC" || importType === "ENTRATA") ? (
                <span style={{ display: 'inline-block' }}>
                  <HelpTooltip text={INVITATION_SOURCE_TOOLTIP_CONTENT} withIcon={false} justifyContent="flex-start">
                    <Link onClick={navTo(invitationSource.url)} sx={{ color: COLOR_SCIENCE_BLUE, display: 'inline' }}>
                      {invitationSource.text}
                    </Link>
                  </HelpTooltip>
                </span>
              ) :
               (importType === "Kinetic") ? (
                <span style={{ display: 'inline-block' }}>
                  <HelpTooltip text={INVITATION_SOURCE_TOOLTIP_CONTENT_KINETIC} withIcon={false} justifyContent="flex-start">
                    <Link onClick={navTo(invitationSource.url)} sx={{ color: COLOR_SCIENCE_BLUE, display: 'inline' }}>
                      {invitationSource.text}
                    </Link>
                  </HelpTooltip>
                </span>
              ) : (importType === "Email") ? (
                <span style={{ display: 'inline-block' }}>
                  <HelpTooltip text={INVITATION_SOURCE_TOOLTIP_CONTENT_EMAIL} withIcon={false} justifyContent="flex-start">
                    <Link onClick={navTo(invitationSource.url)} sx={{ color: COLOR_SCIENCE_BLUE, display: 'inline' }}>
                      {invitationSource.text}
                    </Link>
                  </HelpTooltip>
                </span>
              ) : (
                <Link onClick={navTo(invitationSource.url)} sx={{ color: COLOR_SCIENCE_BLUE }}>
                  {invitationSource.text}
                </Link>
              )}
            </Typography>
          </Grid>
          <Grid item>
            <Typography color={COLOR_STALE_GRAY}>
              <strong>Invite Distribution Method: </strong>
              {inviteDistributionMethod}
            </Typography>
          </Grid>
        </Grid>
        { !areDatesNonEmpty &&
          <Grid container gap={3} alignItems="center">
            <Grid item>
              <HelpTooltip {...YEAR_AROUND_TOOLTIP_CONTENT}>
                <Typography color={COLOR_STALE_GRAY}> Matching Round Open Year Round</Typography>
              </HelpTooltip>
            </Grid>
            <Grid item>
              <Typography color={COLOR_STALE_GRAY} component="span" sx={{ display: 'inline' }}>
                <strong>Invitation Source: </strong>
                {(importType === "ACC" || importType === "ENTRATA") ? (
                  <span style={{ display: 'inline-flex' }}>
                    <HelpTooltip text="If you urgently need to run an import, please contact RoomSync's Customer Support" withIcon={false}>
                      <Link onClick={navTo(invitationSource.url)} sx={{ color: COLOR_SCIENCE_BLUE }}>
                        {invitationSource.text}
                      </Link>
                    </HelpTooltip>
                  </span>
                ) : importType === "Kinetic" ? (
                  <span style={{ display: 'inline-flex' }}>
                    <HelpTooltip text={INVITATION_SOURCE_TOOLTIP_CONTENT_KINETIC} withIcon={false}>
                      <Link onClick={navTo(invitationSource.url)} sx={{ color: COLOR_SCIENCE_BLUE }}>
                        {invitationSource.text}
                      </Link>
                    </HelpTooltip>
                  </span>
                ) : importType === "Email" ? (
                  <span style={{ display: 'inline-flex' }}>
                    <HelpTooltip text={INVITATION_SOURCE_TOOLTIP_CONTENT_EMAIL} withIcon={false}>
                      <Link onClick={navTo(invitationSource.url)} sx={{ color: COLOR_SCIENCE_BLUE }}>
                        {invitationSource.text}
                      </Link>
                    </HelpTooltip>
                  </span>
                ) : (
                  <Link onClick={navTo(invitationSource.url)} sx={{ color: COLOR_SCIENCE_BLUE }}>
                    {invitationSource.text}
                  </Link>
                )}
              </Typography>
            </Grid>
            <Grid item>
              <Typography color={COLOR_STALE_GRAY}>
                <strong>Invite Distribution Method: </strong>
                {inviteDistributionMethod}
              </Typography>
            </Grid>
          </Grid>
        }

        <Grid item>
          <DashboardCard
            percent="0.04"
            title={isIntegratedNetwork? "Invited Users" : "Total Users"}
            count={stats.totalUsers}
            color={COLOR_PACIFIC_BLUE}
            customTooltipText={isIntegratedNetwork? InvitedUsersTooltipText : null}
            icon={<img src="/static/images/icons/total-users.svg" alt="Total Users" width={32} height={32} />}
            footer={
              <Button appearance="link" onClick={navTo(URL.USERS_SEARCH)} iconAfter={<SearchIcon label="" />}>
                Search
              </Button>
            }
          />
        </Grid>
        {!isLoggedInUsersCard && (
          <Grid item>
            <DashboardCard
              percent="0.04"
              title="Registered Users"
              count={stats.loggedInUsers? stats.loggedInUsers : 0}
              color={COLOR_FUCHSIA_BLUE}
              icon={<img src="/static/images/icons/registered-users.svg" alt="Registered Users" width={32} height={32} />}
              footer={
                <Button appearance="link" onClick={navTo(URL.USERS_INVITE)} iconAfter={<SendIcon label="" />}>
                  Invite
                </Button>
              }
            />
          </Grid>
        )}
        <Grid item>
          <DashboardCard
            percent="0.04"
            title="Matched Users"
            color={COLOR_NIAGARA}
            count={stats.matchedUsers}
            helpTooltip={MATCHED_USERS_TOOLTIP_CONTENT}
            icon={<img src="/static/images/icons/matched-users.svg" alt="Matched Users" width={32} height={32} />}
            footer={<MatchedUsersDropdown />}
          />
        </Grid>
        {placementWorksheetEnabled && (
          <Grid item>
            <DashboardCard
              percent="0.04"
              title="Placed Users"
              color={COLOR_TREE_POPPY}
              count={stats.totalPlacedMemberships}
              helpTooltip={PLACED_USERS_TOOLTIP_CONTENT}
              icon={<img src="/static/images/icons/placed-users.svg" alt="Placed Users" width={32} height={32} />}
              footer={
                <Button appearance="link" onClick={navTo(URL.PLACEMENT_WORKSHEET)} iconAfter={<CalendarIcon label="" />}>
                  Worksheet
                </Button>
              }
            />
          </Grid>
        )}
        {apt && (
          <Grid item>
            <DashboardCard
              percent="0.04"
              title="Gained Leases"
              count={stats.generatedLeases}
              color={COLOR_WELL_READ}
              helpTooltip={GENERATED_LEASES_TOOLTIP_CONTENT}
              icon={<img src="/static/images/icons/generated-leases.svg" alt="Generated Leases" width={32} height={32} />}
              footer={
                <Button appearance="link" onClick={handleResourcesClick} iconAfter={<FileIcon label="" />}>
                  Resources
                </Button>
              }
            />
          </Grid>
        )}
      </Grid>

      {/* Warning message for import-based networks with zero invited users */}
      {isImportBasedNetwork(importType) &&
        (status === "ACTIVE" || status === "MATCHING") &&
        stats.totalUsers === 0 && (
        <WarningContainer>
          <WarningIcon label="warning" primaryColor={COLOR_MILANO_RED} />
          <span>
            Your Matching Round has zero imported users. If you have residents with upcoming leases in your system, that should be imported, please contact us{" "}
            <WarningLink href={`mailto:${CLIENTS_EMAIL_ADDRESS}`}>
              {CLIENTS_EMAIL_ADDRESS}
            </WarningLink>
            .
          </span>
        </WarningContainer>
      )}

      {/* Send to Entrata button - only show for Entrata networks with eligible matching rounds */}
      {importType === "ENTRATA" && (status === "CLOSED" || status === "ACTIVE") && (
        <Grid container justifyContent="flex-start" sx={{ mt: 3 }}>
          <Button
            appearance="primary"
            onClick={navTo(URL.SEND_TO_ENTRATA)}
          >
            Send Matches To Entrata
          </Button>
        </Grid>
      )}
    </Container>
    );

};